<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>ボトルキープ</span>
      <v-btn
        color="primary"
        small
        @click="openCreateDialog"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiPlus }}
        </v-icon>
        ボトル追加
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- ローディング表示 -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <!-- ボトル一覧 -->
      <v-data-table
        v-if="bottles.length > 0"
        :headers="headers"
        :items="bottles"
        :footer-props="{ itemsPerPageText: '行/ページ:' }"
        no-data-text="データがありません"
        class="bottle-list-table"
      >
        <template #[`item.name`]="{ item }">
          {{ item.name }}
        </template>

        <template #[`item.tagName`]="{ item }">
          {{ item.tagName || '-' }}
        </template>

        <template #[`item.keptAt`]="{ item }">
          {{ formatDate(item.keptAt) }}
        </template>

        <template #[`item.locationMemo`]="{ item }">
          {{ item.locationMemo || '-' }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            x-small
            @click="openEditDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiPencil }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            color="error"
            @click="confirmDelete(item)"
          >
            <v-icon small>
              {{ icons.mdiDelete }}
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- データなし -->
      <div
        v-else-if="!isLoading"
        class="text-center py-8 grey--text"
      >
        ボトルがありません
      </div>
    </v-card-text>

    <!-- ボトル作成/編集ダイアログ -->
    <customer-bottle-form
      :dialog="bottleFormDialog"
      :editing-bottle="editingBottle"
      @close="closeDialog"
      @save="handleSave"
    />

    <!-- 削除確認ダイアログ -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>ボトルの削除</v-card-title>
        <v-card-text>
          「{{ deleteTargetBottle ? deleteTargetBottle.name : '' }}」を削除してもよろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            :loading="isDeleting"
            @click="handleDelete"
          >
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import { format } from 'date-fns'
import { mdiPlus, mdiPencil, mdiDelete } from '@mdi/js'
import CustomerBottleApi from '@/api/waiter/CustomerBottle'
import CustomerBottleForm from './CustomerBottleForm.vue'

export default {
  name: 'CustomerBottleList',
  components: {
    CustomerBottleForm,
  },
  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
  },
  setup(props, { root }) {
    const bottles = ref([])
    const isLoading = ref(false)
    const bottleFormDialog = ref(false)
    const editingBottle = ref(null)
    const deleteDialog = ref(false)
    const deleteTargetBottle = ref(null)
    const isDeleting = ref(false)

    const headers = [
      { text: 'ボトル名', value: 'name', sortable: true },
      { text: 'タグ名', value: 'tagName', sortable: true },
      { text: 'キープ開始日', value: 'keptAt', sortable: true },
      { text: '配置場所', value: 'locationMemo', sortable: false },
      {
        text: '',
        value: 'actions',
        sortable: false,
        width: '80',
        align: 'end',
      },
    ]

    const formatDate = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd')
      } catch (e) {
        return dateString
      }
    }

    const fetchBottles = async customerId => {
      // customerIdが有効な値かチェック
      const id = Number(customerId)
      if (!id || Number.isNaN(id)) return

      isLoading.value = true
      try {
        const response = await CustomerBottleApi.getCustomerBottles(id)
        // JSONAPI形式: response.data.data が配列
        const data = response?.data?.data || []
        bottles.value = data.map(item => ({
          id: item.id,
          ...item.attributes,
        }))
      } catch (error) {
        console.error('Failed to fetch bottles:', error)
        // 404エラー（顧客が見つからない）の場合はエラー表示しない
        if (error?.response?.status !== 404) {
          root.$toast.error('ボトル一覧の取得に失敗しました')
        }
        bottles.value = []
      } finally {
        isLoading.value = false
      }
    }

    const openCreateDialog = () => {
      editingBottle.value = null
      bottleFormDialog.value = true
    }

    const openEditDialog = bottle => {
      editingBottle.value = { ...bottle }
      bottleFormDialog.value = true
    }

    const closeDialog = () => {
      bottleFormDialog.value = false
      editingBottle.value = null
    }

    const handleSave = async data => {
      try {
        if (editingBottle.value && editingBottle.value.id) {
          // 更新
          await CustomerBottleApi.updateCustomerBottle(
            editingBottle.value.id,
            data,
          )
          root.$toast.success('ボトルを更新しました')
        } else {
          // 作成
          await CustomerBottleApi.createCustomerBottle(props.customerId, data)
          root.$toast.success('ボトルを追加しました')
        }
        closeDialog()
        fetchBottles(props.customerId)
      } catch (error) {
        console.error('Failed to save bottle:', error)
        root.$toast.error('ボトルの保存に失敗しました')
      }
    }

    const confirmDelete = bottle => {
      deleteTargetBottle.value = bottle
      deleteDialog.value = true
    }

    const handleDelete = async () => {
      if (!deleteTargetBottle.value) return

      isDeleting.value = true
      try {
        await CustomerBottleApi.deleteCustomerBottle(
          deleteTargetBottle.value.id,
        )
        root.$toast.success('ボトルを削除しました')
        fetchBottles(props.customerId)
      } catch (error) {
        console.error('Failed to delete bottle:', error)
        root.$toast.error('ボトルの削除に失敗しました')
      } finally {
        isDeleting.value = false
        deleteDialog.value = false
        deleteTargetBottle.value = null
      }
    }

    // customerIdが変更されたら再取得
    watch(() => props.customerId, newId => {
      if (newId) {
        fetchBottles(newId)
      }
    }, { immediate: true })

    return {
      bottles,
      isLoading,
      headers,
      bottleFormDialog,
      editingBottle,
      deleteDialog,
      deleteTargetBottle,
      isDeleting,
      formatDate,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      handleSave,
      confirmDelete,
      handleDelete,
      icons: {
        mdiPlus,
        mdiPencil,
        mdiDelete,
      },
    }
  },
}
</script>

<style scoped>
::v-deep .bottle-list-table .v-data-table__wrapper tbody tr {
  cursor: default;
}
</style>
